// History tab — version timeline with "Compare with previous" diffs of totals.
import { useState } from "react";
import { ChevronDown, ChevronRight, Copy, FilePlus2, History, Lock, PencilLine, RefreshCw, Save } from "lucide-react";
import NoDataState from "modules/swiftboard/components/NoDataState";
import TabSkeleton from "./TabSkeleton";
import { useWorkspace } from "../../../hooks/useEstimateWorkspace";
import { toTotalsSnapshot } from "../../../engine/calcMock";
import { Button, Card, CardHeader, Pill } from "../../../components/ui";
import { fmtCurrency, fmtCurrencyDelta, fmtDateTime, fmtHrs, fmtPctDelta, fmtQty } from "../../../utils/format";

const TYPES = {
  CREATED: { label: "Created", icon: FilePlus2, tone: "primary" },
  DRAFT_SAVED: { label: "Draft saved", icon: Save, tone: "neutral" },
  RECALCULATED: { label: "Recalculated", icon: RefreshCw, tone: "info" },
  FINALIZED: { label: "Finalized (locked)", icon: Lock, tone: "success" },
  DUPLICATED: { label: "Duplicated", icon: Copy, tone: "primary" },
  UNSAVED: { label: "Unsaved changes", icon: PencilLine, tone: "warning" },
};

const ICON_BOX = {
  primary: "bg-primary_tint text-primary border-border",
  neutral: "bg-surface_alt text-text_secondary border-border",
  info: "bg-info_bg text-info border-info_border",
  success: "bg-success_bg text-success border-success_border",
  warning: "bg-warning_bg text-warning border-warning_border",
};

const fmtSignedQty = (n, unit) => `${n >= 0 ? "+" : "−"}${fmtQty(Math.abs(n))}${unit}`;
const fmtSignedHrs = (n) => `${n >= 0 ? "+" : "−"}${fmtHrs(Math.abs(n))} hrs`;

const METRICS = [
  { key: "bidTotal", label: "Bid total", value: fmtCurrency, delta: fmtCurrencyDelta },
  { key: "paintableSf", label: "Paintable SF", value: (n) => `${fmtQty(n)} SF`, delta: (n) => fmtSignedQty(n, " SF") },
  { key: "laborHours", label: "Labor hours", value: (n) => `${fmtHrs(n)} hrs`, delta: fmtSignedHrs },
  { key: "materialCost", label: "Material $", value: fmtCurrency, delta: fmtCurrencyDelta },
  { key: "durationDays", label: "Duration", value: (n) => `${n} days`, delta: (n) => `${n >= 0 ? "+" : "−"}${Math.abs(n)} days` },
];

function DiffTable({ current, previous }) {
  return (
    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-5" role="table" aria-label="Difference from previous version">
      {METRICS.map((m) => {
        const cur = current?.[m.key] || 0;
        const prev = previous?.[m.key] || 0;
        const diff = cur - prev;
        const pct = prev ? diff / prev : 0;
        const same = Math.abs(diff) < 0.005;
        const tone = same ? "text-text_muted" : diff > 0 ? "text-success" : "text-danger";
        return (
          <div key={m.key} role="row" className="rounded-[4px] border border-border_soft bg-surface_alt px-3 py-2">
            <div role="rowheader" className="text-[11px] font-semibold uppercase tracking-wide text-text_muted">{m.label}</div>
            <div role="cell" className="text-[12.5px] tabular-nums text-text_primary">{m.value(cur)}</div>
            <div role="cell" className={`text-[11.5px] font-semibold tabular-nums ${tone}`}>
              {same ? "No change" : `${m.delta(diff)} (${fmtPctDelta(pct)})`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Entry({ entry, previous, isLast, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const t = TYPES[entry.type] || TYPES.DRAFT_SAVED;
  const Icon = t.icon;
  return (
    <li className="relative flex gap-3 pb-5">
      {!isLast && <span className="absolute left-[15px] top-8 bottom-0 w-px bg-border" aria-hidden="true" />}
      <span className={`relative z-[1] flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4px] border ${ICON_BOX[t.tone] || ICON_BOX.neutral}`}>
        <Icon size={15} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Pill tone={t.tone}>{t.label}</Pill>
          <span className="text-[12.5px] font-medium text-text_primary">{entry.user}</span>
          <span className="text-[11.5px] text-text_muted">{entry.at ? fmtDateTime(entry.at) : "Not saved yet"}</span>
          <span className="ml-auto text-[13px] font-semibold tabular-nums text-text_primary">{fmtCurrency(entry.totals?.bidTotal)}</span>
        </div>
        {entry.note && <p className="mt-0.5 text-[12px] text-text_secondary">{entry.note}</p>}
        {previous && (
          <>
            <Button
              size="xs"
              variant="ghost"
              icon={open ? ChevronDown : ChevronRight}
              className="mt-1 -ml-2"
              aria-expanded={open}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setOpen((o) => !o)}
            >
              Compare with previous
            </Button>
            {open && <DiffTable current={entry.totals} previous={previous.totals} />}
          </>
        )}
      </div>
    </li>
  );
}

export default function HistoryTab() {
  const { estimate, calc, dirty, isLocked } = useWorkspace();
  if (!estimate) return <TabSkeleton />;

  const history = estimate.history || [];
  const entries = dirty && calc && !isLocked
    ? [{ id: "unsaved", type: "UNSAVED", user: "You", at: null, note: "Live calculation — Save Draft to keep these changes", totals: toTotalsSnapshot(calc) }, ...history]
    : history;

  return (
    <Card>
      <CardHeader icon={History} title="Version history" subtitle={`${history.length} version${history.length === 1 ? "" : "s"} · newest first`} />
      {entries.length === 0 ? (
        <NoDataState icon="file" title="No history yet" message="Saves, recalculations and finalization will appear here." showBack={false} showRefresh={false} size="small" />
      ) : (
        <ol className="px-4 pt-4" aria-label="Estimate versions">
          {entries.map((e, i) => (
            <Entry key={e.id} entry={e} previous={entries[i + 1]} isLast={i === entries.length - 1} defaultOpen={e.type === "UNSAVED"} />
          ))}
        </ol>
      )}
    </Card>
  );
}
